// Generic stuff for permissions
import React from 'react'
import { Redirect, Route, RouteProps } from 'react-router-dom'

type RequiredPermissionRenderFn = (hasPermission: boolean) => JSX.Element
interface PermissionComposite {
  [key: string]: boolean
}
type Permission = string | PermissionComposite | { anyIn: Permission[] }

export type PermissionRequirement = Permission | Permission[]

// eslint-disable-next-line carsys-custom/typescript-prefer-interface-extend-over-intersection
export type IRequiredPermissionProps<T = {}> = T & {
  permission: PermissionRequirement
  children: React.ReactNode | RequiredPermissionRenderFn
}

/**
 * permissionChecker returns a boolean for every param of whether the shop has permission to use this functionality or not
 *
 * If you provide an array it will check if the module has every single permission in the array
 *
 * If you provide a map it will check whether it matches the permission requirements of the map
 * Example: { PermissionA: true, PermissionB: false } will only return true if the user has
 * PermissionA and doesn't have PermissionB
 *
 * Special case for map with key `anyIn`, Instead of an AND clause will use a OR clause for the permissions requirement.
 * Example: { PermissionA: true, anyIn: { PermissionB: true, PermissionC: false }} will only return true if the user has
 * PermissionA and either (PermissionB or NOT PermissionC)
 */
export const permissionChecker = (grantedPermissions: Permission[], requestedPermissions: PermissionRequirement, value = true): boolean => {
  const itHasSinglePermission = (singlePermission: Permission) => grantedPermissions.includes(singlePermission)

  // when it's a string, it's an actual permission key. this is the exit out of the recursion
  if (typeof requestedPermissions === 'string') {
    return itHasSinglePermission(requestedPermissions) === value
  }

  // when it's an array, every item in the array must be permitted
  if (Array.isArray(requestedPermissions)) {
    return requestedPermissions.every(permission => permissionChecker(grantedPermissions, permission))
  }

  // when anyIn is provided, it's always an array of permissions, of which only one must be permitted
  if (Array.isArray(requestedPermissions.anyIn)) {
    return requestedPermissions.anyIn.some(permission => permissionChecker(grantedPermissions, permission))
  }

  // when it's an object, it's a permission composite
  if (typeof requestedPermissions === 'object') {
    return Object.entries(requestedPermissions).every(([permission, expectedValue]) =>
      permissionChecker(grantedPermissions, permission, expectedValue)
    )
  }

  return false
}

export interface PermissionRouteProps<Path extends string> extends RouteProps<Path> {
  hasPermission: boolean
  permission: PermissionRequirement
  unauthorizedRedirectURL?: string
}

export function PermissionRoute<Path extends string = string>(props: PermissionRouteProps<Path>) {
  const { hasPermission, permission, unauthorizedRedirectURL, ...rest } = props

  if (!hasPermission) {
    console.error(`Expected permission ${JSON.stringify(permission)}`) // stringify because permission might be an object when composed
    return <Redirect to={unauthorizedRedirectURL || '/404'} />
  }

  return <Route {...rest} />
}

export const NewPermissionSuite = (useUserPermissionProvider: () => string[]) => {
  /**
   *  hasPermission validates a singe permissions requirement
   */
  const hasPermission = (userPermissions: string[], permissionsRequirement: PermissionRequirement) =>
    permissionChecker(userPermissions, permissionsRequirement)

  /**
   * useHasPermission hook will validate if all permissionsRequirements are met.
   */
  const useHasPermission = (...permissions: PermissionRequirement[]) => {
    return usePermissionsChecker()(...permissions)
  }

  /**
   * usePermissionsChecker returns a function that will return a boolean for every permission requirement,
   * The boolean will be true/false when the user meets or does not meet the permission requirement.
   */
  const usePermissionsChecker = (): ((...permissions: PermissionRequirement[]) => boolean[]) => {
    const userPermissions = useUserPermissionProvider()
    return (...permissions: PermissionRequirement[]) => permissions.map(permission => hasPermission(userPermissions, permission))
  }

  /**
   * RequiredPermission will hide the children if the user doesn't have the required permission to view this part of the page
   * If you use a render function as child, it pass a boolean with whether or not you have the permission
   */
  const RequiredPermission = <T extends object>(props: IRequiredPermissionProps<T>) => {
    const { permission, children, ...rest } = props

    const [hasPermission] = useHasPermission(permission)

    if (typeof children === 'function') {
      return children(hasPermission)
    }

    if (!hasPermission) {
      return null
    }
    if (!children) {
      return null
    }

    return React.Children.map(children, child => {
      if (React.isValidElement(child)) {
        // pass props to child elements, this resolves issues when wrapping tabs, where the parent component tries to control the childs
        return React.cloneElement(child, { ...rest })
      }
      return child
    })
  }

  return {
    RequiredPermission,
    usePermissionsChecker,
    useHasPermission,
    hasPermission
  }
}
